import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Html5Qrcode } from 'html5-qrcode'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Capacitor } from '@capacitor/core'
import { api } from '../../lib/api'
import { useNetworkStatus } from '../../hooks/useNetworkStatus'
import { collectCameraDiagnostics, reportScanFailure } from '../../lib/cameraDiagnostics'

interface BikeTariff { id: string; name: string; hourlyRate: number; dailyRate?: number | null; crossPointFee?: number | null }
interface Bike {
  id: string; code: string; type?: string | null; model?: string; status: string; qrData: string
  point?: { id: string; name: string }
  tariffId?: string | null
  tariff?: BikeTariff | null
}
interface Shift { id: string; status: string; pointId?: string; point: { id: string; name: string } }
interface LocationState {
  closeMode?: boolean
  addToRentalId?: string
  // New mode: adding a bike to the new rental form (before creating)
  addBikeToNewRental?: boolean
  existingBikes?: Bike[]
  existingBikeIds?: string[]
  formSnapshot?: Record<string, unknown>
}

const STATUS_COLOR: Record<string, string> = {
  free: 'bg-green-100 text-green-700',
  reserved: 'bg-yellow-100 text-yellow-700',
  in_use: 'bg-blue-100 text-blue-700',
  service: 'bg-orange-100 text-orange-700',
}
const STATUS_LABEL: Record<string, string> = {
  free: 'Свободен', reserved: 'Забронирован', in_use: 'В аренде', service: 'Обслуживание',
}

function blockUnavailableBike(bike: Bike, setError: (message: string) => void, setScanning: (value: boolean) => void, resolvedRef: { current: boolean }) {
  const status = STATUS_LABEL[bike.status] ?? bike.status
  setError(`Велосипед #${bike.code} недоступен для открытия аренды: ${status}. Выберите свободный велосипед.`)
  setScanning(false)
  resolvedRef.current = false
}

const QR_READER_ID = 'qr-live-reader'

/**
 * #4 «Диагностика камеры» — one button that tells the manager (and us) exactly why the
 * scanner won't start: HTTPS, permission state, cameras found, and the raw error text.
 * The result is also sent to the server, so we can read it without asking anyone.
 */
function CameraDiagnosticsPanel() {
  const [lines, setLines] = useState<string[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)

  const run = async () => {
    setBusy(true); setSent(false)
    try {
      const diag = await collectCameraDiagnostics({ probe: true, entryPoint: window.location.pathname })
      setLines(diag.summary)
      try {
        await api.post('/diagnostics/scan', { ...diag.payload, manual: true })
        setSent(true)
      } catch { /* showing the result locally is already useful */ }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-700">Камера не работает?</p>
        <button
          onClick={run}
          disabled={busy}
          className="text-xs border border-gray-300 px-3 py-1.5 rounded-xl disabled:opacity-50"
        >
          {busy ? 'Проверяю…' : '🔍 Диагностика камеры'}
        </button>
      </div>
      {lines && (
        <>
          <pre className="text-[11px] whitespace-pre-wrap break-words bg-gray-50 rounded-lg p-2 text-gray-700 max-h-64 overflow-auto">
            {lines.join('\n')}
          </pre>
          <p className="text-[11px] text-gray-400">
            {sent ? 'Отчёт отправлен — можно ничего не пересылать.' : 'Не удалось отправить отчёт: пришлите скриншот этого текста.'}
          </p>
        </>
      )}
    </div>
  )
}

export default function ScanPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()
  const locState = (location.state as LocationState | null) ?? {}
  const closeMode = locState.closeMode ?? false
  const addToRentalId = locState.addToRentalId
  const addBikeToNewRental = locState.addBikeToNewRental ?? false
  const existingBikes = locState.existingBikes ?? []
  const existingBikeIds = locState.existingBikeIds ?? []
  const formSnapshot = locState.formSnapshot
  const { online } = useNetworkStatus()
  const isSecureContext = Capacitor.isNativePlatform() || window.isSecureContext
  const [mode, setMode] = useState<'camera' | 'manual'>(isSecureContext ? 'camera' : 'manual')
  const [manualCode, setManualCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [scanning, setScanning] = useState(false)
  const [cameraReady, setCameraReady] = useState(false)
  const [restartKey, setRestartKey] = useState(0)
  const scannerRef = useRef<Html5Qrcode | null>(null)
  const resolvedRef = useRef(false)
  const scanRequestRef = useRef(false)
  const watchdogRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const restartCountRef = useRef(0)
  const [addedBike, setAddedBike] = useState<{ code: string } | null>(null)

  const addBikeMut = useMutation({
    mutationFn: ({ rentalId, bikeId, tariffId }: { rentalId: string; bikeId: string; tariffId: string }) =>
      api.post(`/rentals/${rentalId}/add-bike`, { bikeId, tariffId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rentals'] }) },
  })

  const { data: shiftData } = useQuery({
    queryKey: ['shift', 'current'],
    queryFn: () => api.get('/shifts/current').then((r) => r.data),
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
  const shift: Shift | null = shiftData?.shift ?? null

  const { data: bikesData, refetch: refetchBikes } = useQuery({
    queryKey: ['bikes', shift?.point?.id],
    queryFn: () => api.get('/bikes', { params: { pointId: shift!.point.id } }).then((r) => r.data),
    enabled: !!shift?.point?.id,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })

  // Live QR scanner with watchdog — works in both browser and Capacitor WebView
  useEffect(() => {
    if (mode !== 'camera') return
    resolvedRef.current = false

    // Ensure the container div exists
    let container = document.getElementById(QR_READER_ID)
    if (!container) {
      container = document.createElement('div')
      container.id = QR_READER_ID
    }

    const scanner = new Html5Qrcode(QR_READER_ID)
    scannerRef.current = scanner
    let started = false

    scanner
      .start(
        { facingMode: 'environment' },
        { fps: 15, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 },
        async (decodedText) => {
          if (resolvedRef.current) return
          resolvedRef.current = true
          try { await scanner.stop() } catch { /* ignore */ }
          await resolveByQr(decodedText)
        },
        undefined,
      )
      .then(() => {
        started = true
        setCameraReady(true)
        restartCountRef.current = 0

        // Watchdog: every 8s check if camera video is still alive
        if (watchdogRef.current) clearInterval(watchdogRef.current)
        watchdogRef.current = setInterval(() => {
          if (resolvedRef.current) return // already handled a scan
          const video = document.querySelector<HTMLVideoElement>(`#${QR_READER_ID} video`)
          const isDead = !video || video.readyState === 0 || video.paused || video.ended
          if (isDead && restartCountRef.current < 5) {
            restartCountRef.current += 1
            // Force restart by bumping the restart key
            setRestartKey((k) => k + 1)
          }
        }, 8000)
      })
      .catch((err: unknown) => {
        // #4: capture the REAL reason instead of swallowing it — it reports itself so
        // nobody has to describe the failure. Never blocks the fallback to manual entry.
        void reportScanFailure({ entryPoint: window.location.pathname, scannerError: err })
        setError('Камера недоступна. Введите код велосипеда вручную или выберите из списка ниже.')
        setMode('manual')
      })

    return () => {
      if (watchdogRef.current) { clearInterval(watchdogRef.current); watchdogRef.current = null }
      if (started) {
        try { scanner.stop().catch(() => {}) } catch { /* scanner was not running */ }
      }
      setCameraReady(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, restartKey])

  const resolveTariffForBike = (bike: Bike): string | null => {
    return bike.tariffId ?? bike.tariff?.id ?? null
  }

  const handleBikeResolved = async (bike: Bike) => {
    // Point check blocks bikes from OTHER points — but NOT when closing (closeMode):
    // a cross-point return is exactly «вернули в другой точке», the bike's pickup
    // point differs from the closing manager's point on purpose. The closeMode
    // branch below still rejects bikes that aren't in an active rental (in_use).
    if (!closeMode && shift?.point?.id && bike.point?.id && bike.point.id !== shift.point.id) {
      setError(`Велосипед #${bike.code} относится к точке «${bike.point.name}», а текущая смена открыта на «${shift.point.name}». Выберите велосипед своей точки.`)
      setScanning(false)
      resolvedRef.current = false
      restartScannerAfterError()
      return
    }

    // Mode 1: add bike to NEW rental form (before creating)
    if (addBikeToNewRental) {
      if (bike.status !== 'free') {
        setError(`Велосипед #${bike.code} не свободен (${STATUS_LABEL[bike.status] ?? bike.status})`)
        setScanning(false); resolvedRef.current = false; return
      }
      if (existingBikeIds.includes(bike.id)) {
        setError(`Велосипед #${bike.code} уже добавлен в аренду`)
        setScanning(false); resolvedRef.current = false; return
      }
      // Preserve «opened via QR» across the add-bike round-trip (#1). Stays true
      // only if it was already true AND this bike was also added by camera scan.
      const prevOpenedViaQr = Boolean(formSnapshot?.openedViaQr)
      navigate('/manager/rental/new', {
        state: {
          bikes: [...existingBikes, bike],
          formSnapshot: { ...formSnapshot, openedViaQr: prevOpenedViaQr && mode === 'camera' },
        },
      })
      return
    }

    // Mode 2: add bike to EXISTING rental (after creating)
    if (addToRentalId) {
      if (bike.status !== 'free') {
        setError(`Велосипед #${bike.code} не свободен (${STATUS_LABEL[bike.status] ?? bike.status})`)
        setScanning(false); resolvedRef.current = false; return
      }
      try {
        const tariffId = resolveTariffForBike(bike)
        if (!tariffId) { setError(`Нет тарифа для велосипеда #${bike.code}. Попросите владельца назначить тариф.`); setScanning(false); resolvedRef.current = false; return }
        await addBikeMut.mutateAsync({ rentalId: addToRentalId, bikeId: bike.id, tariffId })
        setAddedBike({ code: bike.code })
        setScanning(false); resolvedRef.current = false
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Не удалось добавить велосипед'
        setError(msg); setScanning(false); resolvedRef.current = false
      }
      return
    }

    // Default: open new rental OR close existing
    if (closeMode) {
      // In closeMode: only handle bikes that are in active rental
      if (bike.status !== 'in_use') {
        setError(`Велосипед #${bike.code} не в аренде (${STATUS_LABEL[bike.status] ?? bike.status}). Сканируйте велосипед из активной аренды.`)
        setScanning(false)
        resolvedRef.current = false
        restartScannerAfterError()
        return
      }
      // source:'qr' signals the close came from QR — no owner notification
      navigate('/manager/rentals', { state: { openBikeId: bike.id, closeSource: 'qr' } })
      return
    }
    if (bike.status !== 'free') {
      blockUnavailableBike(bike, setError, setScanning, resolvedRef)
      restartScannerAfterError()
      return
    }
    navigate('/manager/rental/new', { state: { bike, openedViaQr: mode === 'camera' } })
  }

  const restartScannerAfterError = () => {
    resolvedRef.current = false
    const sc = scannerRef.current
    if (!sc) { setRestartKey((k) => k + 1); return }
    sc.stop()
      .catch(() => {})
      .finally(() => { setRestartKey((k) => k + 1) })
  }

  const resolveByQr = async (rawQr: string) => {
    if (scanRequestRef.current) return
    let qr = rawQr
    try {
      const url = new URL(rawQr)
      const qrParam = url.searchParams.get('qr')
      if (qrParam) qr = qrParam
    } catch { /* not a URL, use as-is */ }

    if (!online) { setError('Нет подключения — поиск велосипеда недоступен офлайн.'); return }
    scanRequestRef.current = true
    setScanning(true)
    setError(null)
    try {
      await refetchBikes()
      const { data } = await api.get('/bikes/scan', { params: { qr } })
      await refetchBikes()
      await handleBikeResolved(data.bike)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? 'Велосипед не найден. Попробуйте снова или введите код вручную.'
      setError(msg)
      setScanning(false)
      resolvedRef.current = false
      restartScannerAfterError()
    } finally {
      scanRequestRef.current = false
    }
  }

  const resolveByCode = async () => {
    if (scanRequestRef.current) return
    if (!manualCode.trim()) return
    if (!online) { setError('Нет подключения.'); return }
    scanRequestRef.current = true
    setScanning(true)
    setError(null)
    try {
      await refetchBikes()
      const { data } = await api.get('/bikes/scan', { params: { code: manualCode.trim() } })
      await refetchBikes()
      await handleBikeResolved(data.bike)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? `Велосипед "${manualCode}" не найден.`
      setError(msg)
      setScanning(false)
    } finally {
      scanRequestRef.current = false
    }
  }

  const switchToManual = () => {
    try { scannerRef.current?.stop().catch(() => {}) } catch { /* scanner may not be running */ }
    setMode('manual')
    setError(null)
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-900">
      {/* Camera area */}
      {mode === 'camera' && (
        <div className="relative flex-1 min-h-0">
          {/* Live scanner container */}
          <div id={QR_READER_ID} className="w-full h-full" style={{ minHeight: '60vh' }} />

          {/* Scanning overlay */}
          {!cameraReady && !error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black">
              <div className="text-center text-white space-y-3">
                <div className="text-4xl animate-pulse">📷</div>
                <p className="text-sm text-gray-300">Запуск камеры...</p>
              </div>
            </div>
          )}

          {/* Top overlay */}
          <div className="absolute top-0 inset-x-0 bg-gradient-to-b from-black/60 to-transparent p-4 flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="text-white text-xl px-2 py-1">←</button>
            <h2 className="text-white font-semibold text-sm">
              {addBikeToNewRental ? 'Добавить инвентарь' : addToRentalId ? 'Добавить к аренде' : 'Наведите на QR-код велосипеда'}
            </h2>
            <div className="flex gap-2">
              {cameraReady && (
                <button
                  onClick={() => { setCameraReady(false); setRestartKey((k) => k + 1) }}
                  className="text-white text-xs px-2 py-1 border border-white/30 rounded-full opacity-70"
                  title="Перезапустить камеру"
                >
                  🔄
                </button>
              )}
              <button onClick={switchToManual} className="text-white text-xs px-3 py-1 border border-white/50 rounded-full">
                Ввод кода
              </button>
            </div>
          </div>

          {/* Bottom overlay */}
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-5">
            {addedBike && (
              <div className="bg-green-500/90 text-white text-sm rounded-xl px-4 py-3 mb-3 text-center space-y-2">
                <p>Велосипед #{addedBike.code} добавлен к аренде</p>
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => { setAddedBike(null); setRestartKey((k) => k + 1) }}
                    className="bg-white/20 px-4 py-2 rounded-lg text-sm font-medium"
                  >
                    + Ещё велосипед
                  </button>
                  <button
                    onClick={() => navigate('/manager/rentals')}
                    className="bg-white text-green-700 px-4 py-2 rounded-lg text-sm font-bold"
                  >
                    Готово
                  </button>
                </div>
              </div>
            )}
            {scanning && !addedBike && (
              <div className="text-center text-white text-sm mb-3 animate-pulse">🔍 Распознаём...</div>
            )}
            {error && (
              <div className="bg-red-500/90 text-white text-sm rounded-xl px-4 py-3 mb-3 text-center">
                {error}
              </div>
            )}
            <p className="text-center text-gray-400 text-xs">
              Нет QR? <button onClick={switchToManual} className="text-white underline">Введите код вручную</button>
            </p>
          </div>
        </div>
      )}

      {/* Manual mode */}
      {mode === 'manual' && (
        <div className="flex flex-col min-h-screen bg-gray-50">
          <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-gray-400 p-1 text-xl">←</button>
            <h1 className="font-bold text-gray-900 text-sm">Найти велосипед</h1>
            {isSecureContext && (
              <button
                onClick={() => { setMode('camera'); setError(null) }}
                className="ml-auto text-xs border border-brand-400 text-brand-600 px-3 py-1.5 rounded-xl"
              >
                📷 Камера
              </button>
            )}
          </header>

          <div className="flex-1 overflow-auto p-4 space-y-3 pb-32">
            <CameraDiagnosticsPanel />

            {(addToRentalId || addBikeToNewRental) && (
              <div className="bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm text-brand-700 font-medium">
                ➕ {addBikeToNewRental
                  ? `Добавление инвентаря (уже выбрано: ${existingBikes.length})`
                  : 'Добавление велосипеда к аренде'}
              </div>
            )}
            {addedBike && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-700 space-y-2">
                <p className="font-semibold">Велосипед #{addedBike.code} добавлен к аренде</p>
                <div className="flex gap-2">
                  <button onClick={() => setAddedBike(null)} className="flex-1 border border-green-300 py-2 rounded-lg text-sm font-medium">+ Ещё</button>
                  <button onClick={() => navigate('/manager/rentals')} className="flex-1 bg-green-600 text-white py-2 rounded-lg text-sm font-bold">Готово</button>
                </div>
              </div>
            )}
            {!isSecureContext && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-700">
                📷 Камера недоступна в браузере по HTTP. Введите код вручную или выберите из списка. Для сканера используйте приложение (APK).
              </div>
            )}
            {!online && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700">
                ⚡ Офлайн — только список ниже
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Bike list */}
            {shift && bikesData?.bikes && bikesData.bikes.length > 0 && (
              <div className="space-y-2 pt-1">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  Велосипеды точки «{shift.point.name}»
                </p>
                {(bikesData.bikes as Bike[]).map((b) => {
                  const alreadyAdded = existingBikeIds.includes(b.id)
                  const disabled = b.status !== 'free' || scanning || alreadyAdded
                  return (
                    <button
                      key={b.id}
                      disabled={disabled}
                      onClick={async () => {
                        if (scanRequestRef.current) return
                        if (addToRentalId || addBikeToNewRental) {
                          scanRequestRef.current = true
                          setScanning(true)
                          try {
                            await handleBikeResolved(b)
                          } finally {
                            scanRequestRef.current = false
                          }
                        } else {
                          navigate('/manager/rental/new', { state: { bike: b } })
                        }
                      }}
                      className={`w-full flex items-center justify-between bg-white border rounded-xl px-4 py-3 text-left transition-colors ${
                        !disabled
                          ? 'border-gray-200 hover:border-brand-400 active:bg-brand-50'
                          : 'border-gray-100 opacity-60 cursor-not-allowed'
                      }`}
                    >
                      <div>
                        <p className="font-semibold text-sm">#{b.code}{b.model ? ` · ${b.model}` : ''}</p>
                        <p className="text-xs text-gray-400">{b.tariff?.name ?? '—'}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${alreadyAdded ? 'bg-blue-100 text-blue-600' : STATUS_COLOR[b.status] ?? 'bg-gray-100 text-gray-600'}`}>
                        {alreadyAdded ? 'Выбран' : STATUS_LABEL[b.status] ?? b.status}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Fixed bottom input bar */}
          <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 p-4 pb-safe">
            <div className="flex gap-2">
              <input
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="Код велосипеда (напр. P1-001)"
                className="flex-1 rounded-2xl border-2 border-gray-300 px-5 py-4 text-xl font-bold focus:outline-none focus:border-brand-400"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && resolveByCode()}
              />
              <button
                onClick={resolveByCode}
                disabled={scanning || !manualCode.trim() || !online}
                className="bg-brand-600 disabled:opacity-60 text-white font-bold text-base px-6 rounded-2xl flex items-center gap-2"
              >
                {scanning
                  ? <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : 'Найти'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
